'use client';

import Image from 'next/image';
import React, { useCallback, useMemo } from 'react';
import { signIn } from 'next-auth/react';

import googleIcon from './assets/google.svg';
import facebookIcon from './assets/facebook.svg';
import linkedinIcon from './assets/linkedin.svg';

interface SocialLoginButtonProps {
  provider: 'google' | 'facebook' | 'linkedin';
  text: string;
  enabled?: boolean;
  className?: string;
}

export const SocialAuthButton = ({
  provider,
  text,
  className = '',
  enabled = true,
}: SocialLoginButtonProps) => {
  const [isRedirecting, setIsRedirecting] = React.useState(false);

  const baseClass =
    'flex items-center text-sm md:text-md p-2 md:p-2 my-1 md:my-2 rounded-md text-center rounded-lg transition-opacity ';
  const disabledClass =
    !enabled || isRedirecting ? 'opacity-70 cursor-not-allowed' : '';

  const handleSocialLogin = useCallback(async () => {
    if (!enabled || isRedirecting) return;

    setIsRedirecting(true);

    try {
      // Use next-auth signIn for OAuth providers
      await signIn(provider, {
        callbackUrl: '/',
      });
    } catch (error) {
      console.error(`${provider} login error:`, error);
      setIsRedirecting(false);
    }
  }, [provider, enabled, isRedirecting]);

  const providerIcon = useMemo(() => {
    switch (provider) {
      case 'facebook':
        return (
          <Image
            src={facebookIcon as string}
            alt="Facebook Icon"
            className="mr-2"
            width={20}
            height={20}
          />
        );
      case 'google':
        return (
          <Image
            src={googleIcon as string}
            alt="Google Icon"
            className="mr-2"
            width={20}
            height={20}
          />
        );
      case 'linkedin':
      default:
        return (
          <Image
            src={linkedinIcon as string}
            alt="LinkedIn Icon"
            className="mr-2"
            width={20}
            height={20}
          />
        );
    }
  }, [provider]);

  return (
    <button
      onClick={handleSocialLogin}
      className={`${baseClass} ${className} ${disabledClass}`}
      disabled={!enabled || isRedirecting}
      type="button"
      aria-label={text}
    >
      {isRedirecting ? (
        <>
          <svg
            className="animate-spin h-5 w-5 mr-2"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8z"
            />
          </svg>
          <span>Redirecting...</span>
        </>
      ) : (
        <>
          {providerIcon}
          {text}
        </>
      )}
    </button>
  );
};
